import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { get } from '@ember/object';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { ProgressBar } from 'tio-ui/components/status';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import MatchScoreBadge from 'tio-reimagined/components/match-score-badge';
import { eq, gte, not } from 'tio-ui/utilities';
import {
  PhArrowLeft,
  PhArrowRight,
  PhCheckCircle,
  PhClock,
  PhTarget,
  PhLightbulb,
  PhSparkle,
  PhBrain,
  PhChartPie,
  PhTrendUp,
  PhStar,
  PhRocket,
  PhPlay,
  PhQuestion,
  PhFlag,
  PhTrophy,
  PhStethoscope,
  PhClipboardText,
  PhUsers,
  PhChatCircle,
} from 'ember-phosphor-icons';
import type { AssessmentQuestion as ApiAssessmentQuestion } from 'tio-employee/services/career-pathways-api';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';

// Types
interface QuestionOption {
  id: string;
  text: string;
  value: number;
}

interface AssessmentQuestion {
  id: string;
  category: string;
  type: string;
  question: string;
  options: QuestionOption[];
  skillMeasured: string;
}

interface CareerPathwaysSkillsAssessmentSignature {
  Args: {
    model: {
      skillId: string;
      questions: ApiAssessmentQuestion[];
    };
  };
}

// Assessment categories
const HEALTHCARE_ASSESSMENT_CATEGORIES = [
  {
    id: 'clinical',
    name: 'Clinical Skills',
    icon: 'stethoscope',
    questions: 15,
    estimatedTime: '8 min',
    description: 'Patient assessment, clinical judgment, and care delivery skills',
  },
  {
    id: 'leadership',
    name: 'Leadership & Management',
    icon: 'users',
    questions: 12,
    estimatedTime: '6 min',
    description: 'Team leadership, delegation, and management capabilities',
  },
  {
    id: 'communication',
    name: 'Communication',
    icon: 'chat',
    questions: 10,
    estimatedTime: '5 min',
    description: 'Patient, family, and interdisciplinary team communication',
  },
  {
    id: 'technical',
    name: 'Technical & Systems',
    icon: 'clipboard',
    questions: 8,
    estimatedTime: '4 min',
    description: 'EMR proficiency, technology use, and documentation',
  },
];

const UPS_ASSESSMENT_CATEGORIES = [
  {
    id: 'safety',
    name: 'Safety & Compliance',
    icon: 'stethoscope',
    questions: 12,
    estimatedTime: '6 min',
    description: 'Safety protocols, compliance, and risk awareness',
  },
  {
    id: 'leadership',
    name: 'Leadership & Management',
    icon: 'users',
    questions: 10,
    estimatedTime: '5 min',
    description: 'Team leadership, coaching, and accountability',
  },
  {
    id: 'communication',
    name: 'Communication',
    icon: 'chat',
    questions: 8,
    estimatedTime: '4 min',
    description: 'Team communication and shift coordination',
  },
  {
    id: 'operations',
    name: 'Operations & Systems',
    icon: 'clipboard',
    questions: 8,
    estimatedTime: '4 min',
    description: 'Operational workflows and throughput management',
  },
];

const STARBUCKS_ASSESSMENT_CATEGORIES = [
  {
    id: 'customer',
    name: 'Customer Connection',
    icon: 'chat',
    questions: 12,
    estimatedTime: '6 min',
    description: 'Customer experience and service excellence',
  },
  {
    id: 'leadership',
    name: 'Leadership & Coaching',
    icon: 'users',
    questions: 10,
    estimatedTime: '5 min',
    description: 'Team leadership and partner development',
  },
  {
    id: 'operations',
    name: 'Store Operations',
    icon: 'clipboard',
    questions: 9,
    estimatedTime: '5 min',
    description: 'Scheduling, inventory, and quality standards',
  },
  {
    id: 'business',
    name: 'Business Acumen',
    icon: 'stethoscope',
    questions: 7,
    estimatedTime: '4 min',
    description: 'Financial basics and performance metrics',
  },
];

// Assessment results mock
const HEALTHCARE_RESULTS = {
  overall: 78,
  categories: [
    { name: 'Clinical Skills', score: 92, level: 'Expert' },
    { name: 'Leadership & Management', score: 68, level: 'Developing' },
    { name: 'Communication', score: 85, level: 'Advanced' },
    { name: 'Technical & Systems', score: 72, level: 'Intermediate' },
  ],
  strengths: [
    { skill: 'Clinical Judgment', score: 95 },
    { skill: 'Patient Communication', score: 90 },
    { skill: 'Critical Thinking', score: 88 },
  ],
  developmentAreas: [
    { skill: 'Budget Management', score: 35, recommendation: 'Healthcare Finance Basics course' },
    { skill: 'Delegation', score: 52, recommendation: 'Leadership Development Program' },
    {
      skill: 'Staff Scheduling',
      score: 45,
      recommendation: 'Nurse Manager Preparation Workshop',
    },
  ],
  careerFit: [
    { role: 'Nurse Manager', matchScore: 78, gap: 'Leadership skills needed' },
    { role: 'Clinical Nurse Educator', matchScore: 92, gap: 'Strong match!' },
    { role: 'Charge Nurse', matchScore: 95, gap: 'Ready now' },
  ],
};

const UPS_RESULTS = {
  overall: 72,
  categories: [
    { name: 'Safety & Compliance', score: 88, level: 'Advanced' },
    { name: 'Leadership & Management', score: 62, level: 'Developing' },
    { name: 'Communication', score: 74, level: 'Intermediate' },
    { name: 'Operations & Systems', score: 64, level: 'Developing' },
  ],
  strengths: [
    { skill: 'Safety Awareness', score: 90 },
    { skill: 'Team Collaboration', score: 82 },
    { skill: 'Problem Solving', score: 78 },
  ],
  developmentAreas: [
    { skill: 'Leadership', score: 42, recommendation: 'Supervisor Leadership Program' },
    { skill: 'Scheduling', score: 50, recommendation: 'Workforce Planning Workshop' },
    { skill: 'Operational Analytics', score: 48, recommendation: 'Operations Excellence Course' },
  ],
  careerFit: [
    { role: 'Hub Supervisor', matchScore: 74, gap: 'Leadership skills needed' },
    { role: 'Operations Manager', matchScore: 66, gap: 'More analytics experience' },
    { role: 'Feeder Driver (CDL-A)', matchScore: 85, gap: 'License required' },
  ],
};

const STARBUCKS_RESULTS = {
  overall: 80,
  categories: [
    { name: 'Customer Connection', score: 92, level: 'Expert' },
    { name: 'Leadership & Coaching', score: 70, level: 'Developing' },
    { name: 'Store Operations', score: 76, level: 'Advanced' },
    { name: 'Business Acumen', score: 60, level: 'Developing' },
  ],
  strengths: [
    { skill: 'Customer Service', score: 94 },
    { skill: 'Beverage Quality', score: 88 },
    { skill: 'Teamwork', score: 84 },
  ],
  developmentAreas: [
    { skill: 'Financial Management', score: 45, recommendation: 'Retail Finance Basics' },
    { skill: 'Scheduling', score: 52, recommendation: 'Store Operations Certificate' },
    { skill: 'Coaching', score: 58, recommendation: 'Store Manager Development' },
  ],
  careerFit: [
    { role: 'Store Manager', matchScore: 78, gap: 'Leadership skills needed' },
    { role: 'Shift Supervisor', matchScore: 90, gap: 'Strong match!' },
    { role: 'Retail Operations Lead', matchScore: 84, gap: 'Ready now' },
  ],
};

type AssessmentStep = 'intro' | 'questions' | 'results';

export default class CareerPathwaysSkillsAssessment extends Component<CareerPathwaysSkillsAssessmentSignature> {
  get demoClient() {
    return getCurrentDemoClientData();
  }

  get assessmentCategories() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'ups') {
      return UPS_ASSESSMENT_CATEGORIES;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_ASSESSMENT_CATEGORIES;
    }

    return HEALTHCARE_ASSESSMENT_CATEGORIES;
  }

  get mockResults() {
    const clientId = this.demoClient.client.id;

    if (clientId === 'ups') {
      return UPS_RESULTS;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_RESULTS;
    }

    return HEALTHCARE_RESULTS;
  }
  @tracked currentStep: AssessmentStep = 'intro';
  @tracked currentCategory = 0;
  @tracked currentQuestion = 0;
  @tracked answers: Record<string, string> = {};
  @tracked isTimerRunning = false;
  @tracked timeElapsed = 0;
  @tracked showExplanation = false;

  get questions() {
    const skillId = this.args.model.skillId || 'general';
    const skillLabel = skillId
      .split('-')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');

    return this.args.model.questions.map((question) => ({
      id: question.id,
      category: skillId,
      type: 'scenario',
      question: question.question,
      options: question.options.map((option) => ({
        id: option.id,
        text: option.text,
        value: option.value,
      })),
      skillMeasured: skillLabel,
    }));
  }

  get totalQuestions() {
    return this.questions.length;
  }

  get progressPercent() {
    return Math.round((Object.keys(this.answers).length / this.totalQuestions) * 100);
  }

  get currentQuestionData() {
    const fallback: AssessmentQuestion = {
      id: 'placeholder',
      category: 'general',
      type: 'scenario',
      question: 'No questions available for this assessment.',
      options: [{ id: 'ok', text: 'OK', value: 0 }],
      skillMeasured: 'General',
    };
    return this.questions[this.currentQuestion] ?? this.questions[0] ?? fallback;
  }

  get isLastQuestion() {
    return this.currentQuestion === this.totalQuestions - 1;
  }

  get canProceed() {
    return this.answers[this.currentQuestionData.id] !== undefined;
  }

  startAssessment = () => {
    this.currentStep = 'questions';
    this.isTimerRunning = true;
  };

  selectAnswer = (questionId: string, answerId: string) => {
    this.answers = { ...this.answers, [questionId]: answerId };
  };

  nextQuestion = () => {
    if (this.isLastQuestion) {
      this.currentStep = 'results';
      this.isTimerRunning = false;
    } else {
      this.currentQuestion++;
    }
  };

  previousQuestion = () => {
    if (this.currentQuestion > 0) {
      this.currentQuestion--;
    }
  };

  getCategoryIcon = (iconName: string) => {
    const icons: Record<string, unknown> = {
      stethoscope: PhStethoscope,
      users: PhUsers,
      chat: PhChatCircle,
      clipboard: PhClipboardText,
    };
    return icons[iconName] || PhLightbulb;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{#if (eq this.currentStep "intro")}}
        {{! Intro/Landing Page }}
        <div class="text-center mb-8">
          <div
            class="w-[95px] h-[95px] mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"
          >
            <PhBrain @size={{48}} class="text-white" />
          </div>
          <Heading @level={{1}} class="text-3xl font-bold mb-3">Skills Assessment</Heading>
          <Text @intent="muted" class="text-lg max-w-xl mx-auto">
            Discover your strengths, identify growth areas, and get personalized career
            recommendations based on your unique skill profile.
          </Text>
        </div>

        {{! Assessment Overview }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhTarget @size={{20}} class="text-violet-600" />
              <span>What You'll Learn</span>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="text-center p-4">
                <div
                  class="w-[63px] h-[63px] mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center"
                >
                  <PhStar @size={{32}} class="text-green-600" />
                </div>
                <Heading @level={{4}} class="mb-2">Your Strengths</Heading>
                <Text @intent="muted" @size="sm">
                  Identify skills where you excel and can leverage for advancement
                </Text>
              </div>
              <div class="text-center p-4">
                <div
                  class="w-[63px] h-[63px] mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center"
                >
                  <PhTrendUp @size={{32}} class="text-amber-600" />
                </div>
                <Heading @level={{4}} class="mb-2">Growth Areas</Heading>
                <Text @intent="muted" @size="sm">
                  Discover skills to develop with targeted learning recommendations
                </Text>
              </div>
              <div class="text-center p-4">
                <div
                  class="w-[63px] h-[63px] mx-auto mb-3 rounded-full bg-violet-100 flex items-center justify-center"
                >
                  <PhRocket @size={{32}} class="text-violet-600" />
                </div>
                <Heading @level={{4}} class="mb-2">Career Matches</Heading>
                <Text @intent="muted" @size="sm">
                  See which roles align best with your current skill profile
                </Text>
              </div>
            </div>
          </:body>
        </Section>

        {{! Assessment Categories }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <PhChartPie @size={{20}} class="text-violet-600" />
                <span>Assessment Categories</span>
              </div>
              <div class="flex items-center gap-2 text-sm text-gray-500">
                <PhClock @size={{16}} />
                <span>~23 minutes total</span>
              </div>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#each this.assessmentCategories as |category|}}
                <div
                  class="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div
                    class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0"
                  >
                    {{#let (this.getCategoryIcon category.icon) as |Icon|}}
                      {{#if Icon}}
                        {{! @glint-ignore - dynamic icon component }}
                        <Icon @size={{24}} class="text-violet-600" />
                      {{/if}}
                    {{/let}}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between mb-1">
                      <Heading @level={{4}}>{{category.name}}</Heading>
                      <Chip @intent="default">{{category.questions}} questions</Chip>
                    </div>
                    <Text @intent="muted" @size="sm" class="mb-2">{{category.description}}</Text>
                    <Text @intent="muted" @size="xs" class="flex items-center gap-1">
                      <PhClock @size={{12}} />
                      {{category.estimatedTime}}
                    </Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>

        {{! CTA }}
        <div class="text-center">
          <Button
            @intent="primary"
            @size="lg"
            @icon={{PhPlay}}
            @label="Begin Assessment"
            {{on "click" this.startAssessment}}
          />
          <Text @intent="muted" @size="sm" class="mt-3">
            You can pause and resume at any time. Your progress is saved automatically.
          </Text>
        </div>

      {{else if (eq this.currentStep "questions")}}
        {{! Questions Phase }}
        <div class="mb-6">
          {{! Progress Header }}
          <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-4">
                <LinkTo
                  @route="authenticated.reimagined.skills"
                  class="text-gray-500 hover:text-gray-700"
                >
                  <PhArrowLeft @size={{20}} />
                </LinkTo>
                <div>
                  <Text @intent="muted" @size="sm">Question
                    {{this.currentQuestion}}
                    of
                    {{this.totalQuestions}}</Text>
                  <Text class="font-medium">{{this.currentQuestionData.skillMeasured}}</Text>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <Chip
                  @intent={{if
                    (eq this.currentQuestionData.category "leadership")
                    "primary"
                    "default"
                  }}
                >
                  {{this.currentQuestionData.category}}
                </Chip>
                <div class="flex items-center gap-2 text-sm text-gray-500">
                  <PhClock @size={{16}} />
                  <span>{{this.timeElapsed}}s</span>
                </div>
              </div>
            </div>
            <ProgressBar @progress={{this.progressPercent}} @intent="primary" />
          </div>

          {{! Question Card }}
          <Section class="bg-white rounded-xl shadow-sm">
            <:body>
              <div class="mb-6">
                <Chip
                  @intent={{if (eq this.currentQuestionData.type "scenario") "warning" "default"}}
                  class="mb-4"
                >
                  {{#if (eq this.currentQuestionData.type "scenario")}}
                    <span class="inline-flex items-center gap-1">
                      <PhQuestion @size={{14}} />
                      Scenario Question
                    </span>
                  {{else}}
                    <span class="inline-flex items-center gap-1">
                      <PhStar @size={{14}} />
                      Self Assessment
                    </span>
                  {{/if}}
                </Chip>
                <Heading @level={{3}} class="text-xl leading-relaxed">
                  {{this.currentQuestionData.question}}
                </Heading>
              </div>

              <div class="space-y-3">
                {{#each this.currentQuestionData.options as |option|}}
                  <button
                    type="button"
                    {{on "click" (fn this.selectAnswer this.currentQuestionData.id option.id)}}
                    class="w-full text-left p-4 rounded-lg border-2 transition-all
                      {{if
                        (eq (get this.answers this.currentQuestionData.id) option.id)
                        'border-violet-500 bg-violet-50'
                        'border-gray-200 hover:border-violet-300 hover:bg-gray-50'
                      }}"
                  >
                    <div class="flex items-start gap-3">
                      <div
                        class="w-[31px] h-[31px] rounded-full flex items-center justify-center flex-shrink-0 font-semibold
                          {{if
                            (eq (get this.answers this.currentQuestionData.id) option.id)
                            'bg-violet-500 text-white'
                            'bg-gray-200 text-gray-600'
                          }}"
                      >
                        {{option.id}}
                      </div>
                      <span class="flex-1 pt-1">{{option.text}}</span>
                      {{#if (eq (get this.answers this.currentQuestionData.id) option.id)}}
                        <PhCheckCircle @size={{20}} class="text-violet-500 flex-shrink-0" />
                      {{/if}}
                    </div>
                  </button>
                {{/each}}
              </div>
            </:body>
          </Section>

          {{! Navigation }}
          <div class="flex items-center justify-between mt-6">
            <Button
              @intent="default"
              @appearance="outlined"
              @icon={{PhArrowLeft}}
              @label="Previous"
              disabled={{eq this.currentQuestion 0}}
              {{on "click" this.previousQuestion}}
            />
            <div class="flex items-center gap-2">
              {{#each this.questions as |q index|}}
                <div
                  class="w-3 h-3 rounded-full
                    {{if
                      (get this.answers q.id)
                      'bg-violet-500'
                      (if (eq this.currentQuestion index) 'bg-violet-300' 'bg-gray-300')
                    }}"
                ></div>
              {{/each}}
            </div>
            <Button
              @intent="primary"
              @icon={{if this.isLastQuestion PhSparkle PhArrowRight}}
              @iconPlacement="end"
              @label={{if this.isLastQuestion "View Results" "Next"}}
              disabled={{not this.canProceed}}
              {{on "click" this.nextQuestion}}
            />
          </div>
        </div>

      {{else if (eq this.currentStep "results")}}
        {{! Results Phase }}
        <div class="text-center mb-8">
          <div
            class="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center"
          >
            <PhTrophy @size={{40}} class="text-white" />
          </div>
          <Heading @level={{1}} class="text-3xl font-bold mb-2">Assessment Complete!</Heading>
          <Text @intent="muted" class="text-lg">
            Here's your personalized skills analysis and career recommendations
          </Text>
        </div>

        {{! Overall Score }}
        <Section class="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl text-white mb-6">
          <:body>
            <div class="flex items-center justify-between">
              <div>
                <Text class="text-violet-200 mb-1">Overall Skills Score</Text>
                <div class="text-6xl font-bold">{{this.mockResults.overall}}%</div>
                <Text class="text-violet-200 mt-2">Strong foundation with room to grow</Text>
              </div>
              <div class="grid grid-cols-2 gap-4">
                {{#each this.mockResults.categories as |cat|}}
                  <div class="bg-white/10 rounded-lg p-3 text-center min-w-[120px]">
                    <div class="text-2xl font-bold">{{cat.score}}%</div>
                    <Text class="text-violet-200 text-xs">{{cat.name}}</Text>
                  </div>
                {{/each}}
              </div>
            </div>
          </:body>
        </Section>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {{! Strengths }}
          <Section class="bg-white rounded-xl shadow-sm">
            <:header>
              <div class="flex items-center gap-2">
                <PhStar @size={{20}} class="text-green-600" />
                <span>Your Top Strengths</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                {{#each this.mockResults.strengths as |strength|}}
                  <div>
                    <div class="flex justify-between mb-1">
                      <span class="font-medium">{{strength.skill}}</span>
                      <span class="font-bold text-green-600">{{strength.score}}%</span>
                    </div>
                    <ProgressBar @progress={{strength.score}} @intent="success" />
                  </div>
                {{/each}}
              </div>
              <div class="mt-4 p-3 bg-green-50 rounded-lg">
                <Text @size="sm" class="text-green-800">
                  <strong>Great news!</strong>
                  Your strongest skills stand out. Consider mentoring others in these areas.
                </Text>
              </div>
            </:body>
          </Section>

          {{! Development Areas }}
          <Section class="bg-white rounded-xl shadow-sm">
            <:header>
              <div class="flex items-center gap-2">
                <PhTrendUp @size={{20}} class="text-amber-500" />
                <span>Growth Opportunities</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-4">
                {{#each this.mockResults.developmentAreas as |area|}}
                  <div class="p-3 bg-amber-50 rounded-lg">
                    <div class="flex justify-between mb-2">
                      <span class="font-medium">{{area.skill}}</span>
                      <span class="font-bold text-amber-600">{{area.score}}%</span>
                    </div>
                    <ProgressBar @progress={{area.score}} @intent="warning" />
                    <div class="flex items-center gap-2 mt-2 text-sm text-violet-600">
                      <PhSparkle @size={{14}} />
                      <span>{{area.recommendation}}</span>
                    </div>
                  </div>
                {{/each}}
              </div>
            </:body>
          </Section>
        </div>

        {{! Career Fit }}
        <Section class="bg-white rounded-xl shadow-sm mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhRocket @size={{20}} class="text-violet-600" />
              <span>Career Role Fit Analysis</span>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              {{#each this.mockResults.careerFit as |role|}}
                <div
                  class="p-4 rounded-lg border-2 text-center
                    {{if
                      (gte role.matchScore 90)
                      'border-green-500 bg-green-50'
                      (if
                        (gte role.matchScore 75) 'border-violet-500 bg-violet-50' 'border-gray-200'
                      )
                    }}"
                >
                  <MatchScoreBadge @score={{role.matchScore}} @size="lg" class="mx-auto mb-3" />
                  <Heading @level={{4}} class="mb-1">{{role.role}}</Heading>
                  <Text @intent="muted" @size="sm">{{role.gap}}</Text>
                  {{#if (gte role.matchScore 90)}}
                    <Chip @intent="success" class="mt-3">
                      <span class="inline-flex items-center gap-1">
                        <PhCheckCircle @size={{14}} />
                        Excellent Fit
                      </span>
                    </Chip>
                  {{/if}}
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>

        {{! Actions }}
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <LinkTo @route="authenticated.reimagined.skills">
            <Button
              @intent="default"
              @appearance="outlined"
              @icon={{PhArrowLeft}}
              @label="Back to Skills Profile"
            />
          </LinkTo>
          <Button @intent="primary" @icon={{PhRocket}} @label="Create Development Plan" />
          <Button
            @intent="default"
            @appearance="outlined"
            @icon={{PhFlag}}
            @label="Set Career Goal"
          />
        </div>
      {{/if}}
    </VStack>
  </template>
}
